import {
  AfterViewInit,
  Directive,
  ElementRef,
  OnDestroy,
  effect,
  inject,
  input,
  signal,
} from '@angular/core'

import {
  ChatAgentMessageStyles,
  ChatErrorMessageStyles,
  ChatFileAttachmentsStyles,
  ChatFileItemStyles,
  ChatHeaderStyles,
  ChatHistoryItemStyles,
  ChatHistorySectionStyles,
  ChatHistoryStyles,
  ChatInputStyles,
  ChatMessagesStyles,
  ChatStyles,
  ChatSuggestedPromptsStyles,
  ChatUserAttachedFilesStyles,
  ChatUserFileItemStyles,
  ChatUserMessageStyles,
  ChatUserVoiceMessageStyles,
  ChatVoiceInputStyles,
  applyChatAgentMessageCustomStyles,
  applyChatCustomStyles,
  applyChatErrorMessageCustomStyles,
  applyChatFileAttachmentsCustomStyles,
  applyChatFileItemCustomStyles,
  applyChatHeaderCustomStyles,
  applyChatHistoryCustomStyles,
  applyChatInputCustomStyles,
  applyChatMessagesCustomStyles,
  applyChatSuggestedPromptsCustomStyles,
  applyChatUserAttachedFilesCustomStyles,
  applyChatUserFileItemCustomStyles,
  applyChatUserMessageCustomStyles,
  applyChatUserVoiceMessageCustomStyles,
  applyChatVoiceInputCustomStyles,
} from '@flowx/core-theme'

type ComponentStyleInput = {
  style: ChatStyles
  headerStyle: ChatHeaderStyles
  messagesStyle: ChatMessagesStyles
  agentMessageStyle: ChatAgentMessageStyles
  userMessageStyle: ChatUserMessageStyles
  userVoiceMessageStyle: ChatUserVoiceMessageStyles
  errorMessageStyle: ChatErrorMessageStyles
  inputStyle: ChatInputStyles
  historyStyle: ChatHistoryStyles
  historyItemStyle: ChatHistoryItemStyles
  historySectionStyle: ChatHistorySectionStyles
  suggestedPromptsStyle?: ChatSuggestedPromptsStyles
  voiceInputStyle?: ChatVoiceInputStyles
  inputFileAttachmentsStyle?: ChatFileAttachmentsStyles
  inputFileItemStyle?: ChatFileItemStyles
  userFileAttachmentsStyle?: ChatUserAttachedFilesStyles
  userFileItemStyle?: ChatUserFileItemStyles
}

type StyleMapping = {
  selector: string
  styleKey: keyof ComponentStyleInput
  applyFn: (styles: any) => string
}

const STATIC_STYLES: StyleMapping[] = [
  { selector: 'flx-chat-header', styleKey: 'headerStyle', applyFn: applyChatHeaderCustomStyles },
  {
    selector: '.flx-chat-messages',
    styleKey: 'messagesStyle',
    applyFn: applyChatMessagesCustomStyles,
  },
  {
    selector: '.flx-chat-messages',
    styleKey: 'userVoiceMessageStyle',
    applyFn: applyChatUserVoiceMessageCustomStyles,
  },
  {
    selector: '.flx-chat-messages',
    styleKey: 'userFileAttachmentsStyle',
    applyFn: applyChatUserAttachedFilesCustomStyles,
  },
  {
    selector: '.flx-chat-messages',
    styleKey: 'userFileItemStyle',
    applyFn: applyChatUserFileItemCustomStyles,
  },
  { selector: 'flx-chat-input', styleKey: 'inputStyle', applyFn: applyChatInputCustomStyles },
  {
    selector: 'flx-chat-input',
    styleKey: 'voiceInputStyle',
    applyFn: applyChatVoiceInputCustomStyles,
  },
  {
    selector: 'flx-chat-input',
    styleKey: 'inputFileAttachmentsStyle',
    applyFn: applyChatFileAttachmentsCustomStyles,
  },
  {
    selector: 'flx-chat-input',
    styleKey: 'inputFileItemStyle',
    applyFn: applyChatFileItemCustomStyles,
  },
  {
    selector: '.flx-chat-history-content',
    styleKey: 'historyStyle',
    applyFn: applyChatHistoryCustomStyles,
  },
]

const DYNAMIC_STYLES: (StyleMapping & { multi: boolean })[] = [
  {
    selector: '.flx-chat-message-receiver',
    styleKey: 'agentMessageStyle',
    applyFn: applyChatAgentMessageCustomStyles,
    multi: true,
  },
  {
    selector: '.flx-chat-message-sender',
    styleKey: 'userMessageStyle',
    applyFn: applyChatUserMessageCustomStyles,
    multi: true,
  },
  {
    selector: '.flx-chat-message-error',
    styleKey: 'errorMessageStyle',
    applyFn: applyChatErrorMessageCustomStyles,
    multi: true,
  },
  {
    selector: '.flx-chat-suggested-prompts',
    styleKey: 'suggestedPromptsStyle',
    applyFn: applyChatSuggestedPromptsCustomStyles,
    multi: false,
  },
]

@Directive({
  selector: 'flx-chat',
  host: {
    '[class]': 'styleClassName()',
  },
})
export class FlxChatStyleDirective implements AfterViewInit, OnDestroy {
  #element = inject(ElementRef)

  #messageObserver: MutationObserver | null = null
  #childObserver: MutationObserver | null = null

  #staticClassNames = new Map<string, string>()
  #dynamicClassNames = new Map<string, string>()

  componentStyle = input<ComponentStyleInput>()

  styleClassName = signal<string>('')

  constructor() {
    effect(() => {
      const componentStyle = this.componentStyle()
      this.#applyStyles(componentStyle)
    })
  }

  ngAfterViewInit(): void {
    const componentStyle = this.componentStyle()
    if (componentStyle) {
      this.#applyStyles(componentStyle)
    }

    this.#childObserver = new MutationObserver(() => {
      const componentStyle = this.componentStyle()
      if (componentStyle) {
        this.#applyStyles(componentStyle)
      }
      this.#setupMessageObserver()
    })
    this.#childObserver.observe(this.#element.nativeElement, { childList: true })

    this.#setupMessageObserver()
  }

  ngOnDestroy(): void {
    this.#messageObserver?.disconnect()
    this.#messageObserver = null
    this.#childObserver?.disconnect()
    this.#childObserver = null
  }

  #applyStyles(componentStyle: NonNullable<ComponentStyleInput>): void {
    this.styleClassName.set(applyChatCustomStyles(componentStyle.style))

    const host = this.#element.nativeElement

    for (const { selector, styleKey, applyFn } of STATIC_STYLES) {
      const newClassName = applyFn(componentStyle[styleKey])
      const element = host.querySelector(selector)
      if (element) {
        this.#swapClass(element, this.#staticClassNames.get(styleKey) ?? '', newClassName)
      }
      this.#staticClassNames.set(styleKey, newClassName)
    }

    this.#swapDynamicStyles(componentStyle)
  }

  #swapDynamicStyles(componentStyle: NonNullable<ComponentStyleInput>): void {
    const host = this.#element.nativeElement

    for (const { selector, styleKey, applyFn, multi } of DYNAMIC_STYLES) {
      const oldClassName = this.#dynamicClassNames.get(styleKey) ?? ''
      const newClassName = applyFn(componentStyle[styleKey])

      if (oldClassName.length > 0) {
        if (multi) {
          host
            .querySelectorAll(selector)
            .forEach((el: HTMLElement) => el.classList.remove(oldClassName))
        } else {
          host.querySelector(selector)?.classList.remove(oldClassName)
        }
      }

      if (newClassName.length > 0) {
        if (multi) {
          host
            .querySelectorAll(selector)
            .forEach((el: HTMLElement) => el.classList.add(newClassName))
        } else {
          host.querySelector(selector)?.classList.add(newClassName)
        }
      }

      this.#dynamicClassNames.set(styleKey, newClassName)
    }
  }

  #applyDynamicStyles(): void {
    const host = this.#element.nativeElement

    for (const { selector, styleKey, multi } of DYNAMIC_STYLES) {
      const className = this.#dynamicClassNames.get(styleKey) ?? ''
      if (className.length > 0) {
        if (multi) {
          host.querySelectorAll(selector).forEach((el: HTMLElement) => el.classList.add(className))
        } else {
          host.querySelector(selector)?.classList.add(className)
        }
      }
    }
  }

  #swapClass(element: HTMLElement, oldClassName: string, newClassName: string): void {
    if (oldClassName.length > 0) {
      element.classList.remove(oldClassName)
    }
    if (newClassName.length > 0) {
      element.classList.add(newClassName)
    }
  }

  #setupMessageObserver(): void {
    this.#messageObserver?.disconnect()
    this.#messageObserver = null

    const messagesContainer = this.#element.nativeElement.querySelector('.flx-chat-messages')
    if (!messagesContainer) {
      return
    }

    this.#messageObserver = new MutationObserver(() => this.#applyDynamicStyles())
    this.#messageObserver.observe(messagesContainer, { childList: true, subtree: true })
  }
}
